import { Feature, Geometry, GeoJsonProperties } from 'geojson';
import { LatLngExpression, Layer, LeafletMouseEventHandlerFn, PathOptions, StyleFunction } from 'leaflet';
import { FC, useEffect, useState } from 'react';
import { GeoJSON } from 'react-leaflet';

import {
  FEATURE_DEFAULT_COLOR,
  FEATURE_DEFAULT_WIDTH,
  FEATURE_HIGHLIGHTED_COLOR,
  FEATURE_HIGHLIGHTED_WIDTH,
  FEATURE_OPACITY,
} from '@common/constants';
import { MapFeature, MapFeatureConfig, MapFeatureGeometry } from '@common/types';
import { getCenterByCoordinates } from '@common/utils';

import Map from '../Map/Map';
import { HighlightedFeature, MapChoroplethProps } from './MapChoropleth.types';

const MapChoropleth: FC<MapChoroplethProps<HighlightedFeature>> = ({
  bounds,
  featuresList,
  legend,
  renderPopup,
  ...props
}) => {
  const [highlightedFeature, setHighlightedFeature] = useState<HighlightedFeature | undefined>();
  const [markers, setMarkers] = useState<LatLngExpression[] | undefined>();
  const [legendColors, setLegendColors] = useState<string[] | undefined>();

  /* this function is called when a feature in the map is hovered over or when a mouse moves out of it, the function calls two functions
     highlightFeature and resetHighlight */
  const onEachFeature = (feature: Feature<Geometry, GeoJsonProperties>, layer: Layer): void => {
    layer.on({
      mouseover: highlightFeature,
      mouseout: resetHighlight,
    });
  };

  const styleFeature = (feature: Feature<Geometry, any> | undefined, featuresList: MapFeatureConfig): PathOptions => {
    const isFeatureHighlighted = highlightedFeature && highlightedFeature?.id === feature?.id;
    const fillColor = feature?.properties?.fillColor ?? featuresList.fillColor;

    return {
      fillColor,
      weight: isFeatureHighlighted ? FEATURE_HIGHLIGHTED_WIDTH : FEATURE_DEFAULT_WIDTH,
      opacity: FEATURE_OPACITY,
      color: isFeatureHighlighted ? FEATURE_HIGHLIGHTED_COLOR : FEATURE_DEFAULT_COLOR,
      fillOpacity: FEATURE_OPACITY,
    };
  };

  /* resets our state i.e no properties should be displayed when a feature is not clicked or hovered over */
  const resetHighlight: LeafletMouseEventHandlerFn = (_e) => {
    setHighlightedFeature(undefined);
  };

  /* function determining what should happen onmouseover, this function updates our state */
  const highlightFeature: LeafletMouseEventHandlerFn = (e) => {
    const { name, value } = e.target.feature?.properties;
    if (!value) {
      return;
    }

    setHighlightedFeature({
      id: e.target.feature.id,
      name,
      value,
      position: e.latlng,
    });
  };

  useEffect(() => {
    // if no features, then stop calculation.
    if (featuresList?.every((f: MapFeatureConfig) => f.features.length === 0)) {
      return;
    }

    const featuresWithMarkers: MapFeatureConfig[] = featuresList?.filter(
      (featureConfig: MapFeatureConfig) => !!featureConfig.markers,
    );

    const markers: LatLngExpression[] = [];
    featuresWithMarkers?.forEach((featureConfig: MapFeatureConfig) => {
      featureConfig.features.forEach((feature: Feature<Geometry, GeoJsonProperties> | MapFeature) => {
        if (!feature.geometry) return;
        markers.push(getCenterByCoordinates((feature.geometry as MapFeatureGeometry)?.coordinates[0]));
      });
    });

    setMarkers(markers);
  }, [featuresList]);

  useEffect(() => {
    if (!legend) return;

    const featureWithColors: MapFeatureConfig | undefined = featuresList?.find(
      (featureConfig: MapFeatureConfig) => !!featureConfig.colors,
    );
    const colorRanges = featureWithColors?.colors;
    colorRanges && setLegendColors(colorRanges);
  }, [featuresList, legend]);

  return bounds ? (
    <Map
      {...props}
      legend={legend && { ...legend, colors: legendColors }}
      popupContent={highlightedFeature && renderPopup?.(highlightedFeature)}
      bounds={bounds}
      markers={markers}
      showPopup={!!highlightedFeature}
      popupPosition={highlightedFeature?.position}
    >
      {featuresList?.map((featuresListItem: MapFeatureConfig, index: number) => {
        return (
          <GeoJSON
            key={index}
            data={featuresListItem}
            style={((feature) => styleFeature(feature, featuresListItem)) as StyleFunction}
            onEachFeature={onEachFeature}
          />
        );
      })}
    </Map>
  ) : null;
};

export default MapChoropleth;
